import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import LazyLoad from 'react-lazyload';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import heroImage1 from '../assets/111.webp';
import heroImage2 from '../assets/1111.webp';
import heroImage3 from '../assets/11111.webp';
import heroImage4 from '../assets/111111.webp';
import {
  ArrowRight,
  CheckCircle,
  Shield,
  MapPin,
  Wind,
  Wrench,
  Layers,
  FlaskConical,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import QuoteModal from '../components/QuoteModal';
import { handleWhatsAppRedirect } from '../helper/whatsapp';
// ✅ Same data source as Products page and ProductDetail page
import PRODUCTS from '../data/products';

// ─── Container ────────────────────────────────────────────────────────────────
const Container = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

// ─── Product Categories ───────────────────────────────────────────────────────
const productCategories = [
  {
    id: 'storage-tanks',
    name: 'Storage & Handling',
    description:
      'FRP storage tanks built for chemical, pharma, water treatment, and heavy industrial environments.',
    icon: Layers,
    color: 'bg-blue-600',
    link: '/products',
  },
  {
    id: 'air-fume-control',
    name: 'Air & Fume Control',
    description:
      'FRP scrubbers, stacks, and duct systems engineered for corrosive fume extraction and emission control.',
    icon: Wind,
    color: 'bg-green-600',
    link: '/products',
  },
  {
    id: 'plant-fabrication',
    name: 'Plant Fabrication',
    description:
      'Custom FRP plant equipment including vessels, pipes, fittings, and structural components.',
    icon: Wrench,
    color: 'bg-yellow-600',
    link: '/products',
  },
  {
    id: 'industrial-frp',
    name: 'Industrial FRP Excellence',
    description:
      'Precision-engineered FRP solutions using HDPE, PP, PPFRP/PVC for diverse industrial needs.',
    icon: FlaskConical,
    color: 'bg-purple-600',
    link: '/products',
  },
];

// ─── About Stats ──────────────────────────────────────────────────────────────
const stats = [
  { value: '25+', label: 'Years of Experience' },
  { value: '1000+', label: 'Happy Clients' },
  { value: 'ISO', label: 'Certified Quality' },
  { value: 'Pan-India', label: 'Supply Capability' },
];

// ─── Image Slider Hook ────────────────────────────────────────────────────────
function useImageSlider(images: string[], interval = 5000) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, interval);
    return () => clearInterval(timer);
  }, [images.length, interval]);

  const prev = () =>
    setCurrent((p) => (p === 0 ? images.length - 1 : p - 1));
  const next = () =>
    setCurrent((p) => (p === images.length - 1 ? 0 : p + 1));

  return { current, setCurrent, prev, next };
}

// ─── Main Component ───────────────────────────────────────────────────────────
const Index = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Hero section specific data
  const heroImages = [
    { src: heroImage1, alt: "FRP Storage Tanks", badge: "Storage Solutions", name: "Advanced FRP Storage Systems", description: "High-capacity corrosion-resistant storage tanks for industrial applications", certification: "ISO 9001" },
    { src: heroImage2, alt: "FRP Piping Systems", badge: "Piping Solutions", name: "Industrial FRP Piping Systems", description: "Complete piping solutions for corrosive fluid transport", certification: "ISO 9001" },
    { src: heroImage3, alt: "FRP Scrubbers", badge: "Air Treatment", name: "FRP Scrubbers & Stack Systems", description: "Custom-designed air pollution control systems", certification: "CPCB" },
    { src: heroImage4, alt: "FRP Vessels", badge: "Process Equipment", name: "Custom FRP Process Vessels", description: "Tailored vessels for chemical processing and storage", certification: "ASME" }
  ];
const {
  current: currentImageIndex,
  setCurrent: setCurrentImageIndex,
  prev,
  next,
} = useImageSlider(
  [heroImage1, heroImage2, heroImage3, heroImage4],
  5000
);

  // ✅ Take first 6 products from shared data for home page
  const featuredProducts = PRODUCTS.slice(0, 6);

  return (
    <>
      {/* ── SEO ── */}
      <Helmet>
        <title>
          SS Plastic | FRP Equipment & Corrosion-Resistant Systems | Vapi,
          Gujarat
        </title>
        <meta
          name="description"
          content="SS Plastic is a leading FRP manufacturer in India offering precision-engineered fiberglass tanks, scrubbers, pipes, and industrial FRP solutions. Based in Vapi, Gujarat."
        />
        <meta
          name="keywords"
          content="FRP manufacturer India, fiberglass tanks, FRP scrubbers, industrial FRP, corrosion resistant tanks, Vapi Gujarat, SS Plastic"
        />
        <meta
          property="og:title"
          content="SS Plastic | FRP Equipment & Corrosion-Resistant Systems"
        />
        <meta
          property="og:description"
          content="Trusted industrial FRP fabrication — tanks, scrubbers, ductings, pipes, and vessels built for durability. Serving chemical, pharma & wastewater industries."
        />
        <meta property="og:image" content="/ssplasticlogo.webp" />
        <meta property="og:url" content="https://www.ss-plastic.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SS Plastic | FRP Equipment & Corrosion-Resistant Systems" />
        <meta name="twitter:description" content="Trusted industrial FRP fabrication — tanks, scrubbers, ductings, pipes, and vessels built for durability. Serving chemical, pharma & wastewater industries." />
        <meta name="twitter:image" content="/ssplasticlogo.webp" />
        <link rel="canonical" href="https://www.ss-plastic.com" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'SS Plastic',
            url: 'https://www.ss-plastic.com',
            description:
              'Leading FRP manufacturer in India offering precision-engineered solutions for industrial applications.',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Vapi',
              addressRegion: 'Gujarat',
              addressCountry: 'IN',
            },
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+91-9825850501',
              contactType: 'Customer Service',
            },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen">
        {/* ── TOP INFO BAR ── */}
        {/* <div className="bg-egyptian-blue text-white text-xs sm:text-sm py-2 px-4 hidden md:block">
          <Container>
            <div className="flex justify-between items-center">
              <div className="flex gap-6">
                <a
                  href="tel:+919825850501"
                  className="flex items-center gap-2 hover:text-amber transition-colors"
                >
                  <Phone className="h-3 w-3" />
                  +91 98258 50501
                </a>
                <a
                  href="mailto:info@ss-plastic.com"
                  className="flex items-center gap-2 hover:text-amber transition-colors"
                >
                  <Mail className="h-3 w-3" />
                  info@ss-plastic.com
                </a>
                <span className="flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  Vapi, Gujarat, India
                </span>
              </div>
              <span className="text-white/70">GST: 24BIRPS4800G2Z3</span>
            </div>
          </Container>
        </div> */}

{/* ── HERO SECTION ── */}
<section className="relative min-h-[620px] sm:min-h-[680px] lg:min-h-[720px] flex items-center overflow-hidden">
  {/* Background Images */}
  {heroImages.map((hero, index) => (
    <div
      key={index}
      className={`absolute inset-0 transition-opacity duration-1000 ${
        index === currentImageIndex ? "opacity-100" : "opacity-0"
      }`}
    >
      <img
        src={hero.src}
        alt={hero.alt}
        className="w-full h-full object-cover"
      />
    </div>
  ))}

  {/* Blue Overlays */}
  <div className="absolute inset-0 bg-gradient-to-r from-blue-950/90 via-blue-900/70 to-blue-800/40" />
  <div className="absolute inset-0 bg-gradient-to-t from-blue-950/80 via-transparent to-blue-900/20" />

  {/* Glow Effects */}
  <div className="absolute top-0 left-0 w-80 h-80 bg-blue-600/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
  <div className="absolute bottom-0 right-0 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none" />

  {/* Left Accent */}
  {/* <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-blue-400 via-amber to-blue-600 hidden sm:block" /> */}

  {/* Arrows */}
  <button
    onClick={prev}
    className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-blue-900/50 backdrop-blur-sm border border-blue-400/30 flex items-center justify-center hover:bg-blue-600 hover:border-blue-400 transition-all duration-300"
    aria-label="Previous"
  >
    <ChevronLeft className="h-5 w-5 text-white" />
  </button>

  <button
    onClick={next}
    className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-blue-900/50 backdrop-blur-sm border border-blue-400/30 flex items-center justify-center hover:bg-blue-600 hover:border-blue-400 transition-all duration-300"
    aria-label="Next"
  >
    <ChevronRight className="h-5 w-5 text-white" />
  </button>

  {/* Content */}
  <div className="relative z-10 w-full py-14 sm:py-16 lg:py-20">
    <Container>
      <div className="max-w-4xl">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-amber text-black text-[11px] sm:text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded mb-4 shadow-lg shadow-amber/20">
          <span className="w-1.5 h-1.5 rounded-full bg-black/60" />
          {heroImages[currentImageIndex]?.badge}
        </div>

        {/* Title */}
        <h1 className="font-display font-bold text-3xl sm:text-5xl lg:text-6xl text-white leading-[1.1] mb-4 max-w-2xl">
          {heroImages[currentImageIndex]?.name}
        </h1>

        {/* Decorative Line */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-1 bg-amber rounded-full" />
          <div className="w-5 h-1 bg-blue-400 rounded-full" />
          <div className="w-2 h-1 bg-blue-300/60 rounded-full" />
        </div>

        {/* Description */}
        <p className="text-sm sm:text-lg text-blue-100/90 leading-relaxed max-w-2xl mb-5">
          {heroImages[currentImageIndex]?.description}
        </p>

        {/* Certification */}
        <div className="inline-flex items-center gap-2 text-blue-100 text-sm font-medium mb-6 border border-blue-400/30 bg-blue-600/20 backdrop-blur-sm px-4 py-2 rounded-full">
          <Shield className="h-4 w-4 text-amber" />
          {heroImages[currentImageIndex]?.certification} Certified
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8">
          <Button
            variant="cta"
            size="lg"
            asChild
            className="px-7 py-3 text-base font-semibold group"
          >
            <Link to="/products">
              View Products
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>

          <Button
            size="lg"
            className="px-7 py-3 text-base font-semibold bg-blue-600/30 text-white border border-blue-400/40 hover:bg-blue-600/60 hover:border-blue-400 backdrop-blur-sm"
            onClick={() => setIsModalOpen(true)}
          >
            Get a Quote
          </Button>
        </div>

        {/* Compact Stats Bar */}
        <div className="rounded-2xl border border-blue-400/20 bg-white/10 backdrop-blur-md p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-4 sm:gap-x-7">
            <div className="flex items-center gap-2.5">
              <div className="text-2xl sm:text-3xl font-bold text-amber">25+</div>
              <div className="text-[11px] text-blue-100/70 leading-tight">
                Years of<br />Experience
              </div>
            </div>

            <div className="w-px h-8 bg-blue-300/20 hidden sm:block" />

            <div className="flex items-center gap-2.5">
              <div className="text-2xl sm:text-3xl font-bold text-amber">1000+</div>
              <div className="text-[11px] text-blue-100/70 leading-tight">
                Happy<br />Clients
              </div>
            </div>

            <div className="w-px h-8 bg-blue-300/20 hidden sm:block" />

            <div className="flex items-center gap-2.5">
              <div className="text-2xl sm:text-3xl font-bold text-amber">50+</div>
              <div className="text-[11px] text-blue-100/70 leading-tight">
                FRP<br />Products
              </div>
            </div>

            <div className="w-px h-8 bg-blue-300/20 hidden sm:block" />

            <div className="flex items-center gap-2 text-sm text-blue-100/80 bg-blue-600/20 border border-blue-400/20 px-3 py-1.5 rounded-full">
              <Shield className="h-4 w-4 text-amber" />
              <span>ISO Certified</span>
            </div>

            <div className="flex items-center gap-2 text-sm text-blue-100/80 bg-blue-600/20 border border-blue-400/20 px-3 py-1.5 rounded-full">
              <MapPin className="h-4 w-4 text-amber" />
              <span>Vapi, Gujarat</span>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </div>

  {/* Dots */}
  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
    {heroImages.map((_, i) => (
      <button
        key={i}
        onClick={() => setCurrentImageIndex(i)}
        className={`rounded-full transition-all duration-300 ${
          i === currentImageIndex
            ? "bg-amber w-10 h-2.5"
            : "bg-blue-200/30 w-2.5 h-2.5 hover:bg-blue-200/60"
        }`}
        aria-label={`Slide ${i + 1}`}
      />
    ))}
  </div>

  {/* Counter */}
  <div className="absolute bottom-4 right-6 text-blue-200/50 text-sm z-20 hidden sm:block">
    <span className="text-amber font-bold text-lg">
      {String(currentImageIndex + 1).padStart(2, "0")}
    </span>
    <span className="mx-1">/</span>
    <span>{String(heroImages.length).padStart(2, "0")}</span>
  </div>
</section>

        {/* ── PRODUCT CATEGORY CARDS ── */}
        <LazyLoad height={200} offset={100}>
          <section className="py-10 sm:py-16 bg-white">
            <Container>
              <div className="text-center mb-12">
                <Badge className="mb-4 text-amber border-amber bg-amber/10 px-4 py-2 text-sm font-medium">
                  Industrial FRP Solutions
                </Badge>
                <h2 className="font-display font-bold text-3xl sm:text-5xl text-egyptian-blue mb-4 fade-in-up">
                  Our Product Categories
                </h2>
                <p className="text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed fade-in-up">
                  Comprehensive FRP solutions engineered for industrial strength, 
                  reliability, and superior corrosion resistance across every application.
                </p>
              </div>

             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
  {productCategories.map((cat, index) => (
    <Card
      key={cat.id}
      className="card-industrial group fade-in-up h-full flex flex-col"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <CardContent className="p-6 text-center flex flex-col h-full">
        {/* Icon */}
        <div className="flex justify-center mb-5 shrink-0">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 ${cat.color} shadow-lg`}
          >
            <cat.icon className="h-8 w-8 text-white" />
          </div>
        </div>

        {/* Title */}
        <div className="min-h-[56px] flex items-center justify-center mb-3">
          <h3 className="font-display font-bold text-base sm:text-lg text-egyptian-blue leading-snug group-hover:text-violet-blue transition-all duration-300">
            {cat.name}
          </h3>
        </div>

        {/* Description */}
        <div className="min-h-[88px] flex items-start justify-center mb-5">
          <p className="text-gray-600 text-sm leading-relaxed">
            {cat.description}
          </p>
        </div>

        {/* Button */}
        <div className="mt-auto">
          <Button
            variant="enterprise"
            size="sm"
            className="w-full button-glow group"
            asChild
          >
            <Link to={cat.link}>
              View Products
              <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  ))}
</div>
            </Container>
          </section>
        </LazyLoad>

        {/* ── ABOUT / TRUST SECTION ── */}
        <section className="py-10 sm:py-16 bg-gradient-trust px-4">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              {/* Left */}
              <div>
                <Badge
                  variant="outline"
                  className="mb-3 text-egyptian-blue border-egyptian-blue"
                >
                  About SS Plastic
                </Badge>
                <h2 className="font-display font-bold text-2xl sm:text-4xl text-egyptian-blue mb-4">
                  Leading FRP Manufacturer in India
                </h2>
                <p className="text-gray-700 mb-5 text-sm sm:text-base leading-relaxed">
                  SS Plastic is a leading FRP (Fiber Reinforced Plastic)
                  manufacturer based in Vapi, Gujarat, India. We specialize in
                  designing and manufacturing high-quality fiberglass products
                  for various industrial applications.
                </p>
                <p className="text-gray-700 mb-6 text-sm sm:text-base leading-relaxed">
                  With extensive experience in working with materials like HDPE,
                  PP, and PPFRP/PVC, we handle the entire fabrication process —
                  from mold creation to the final assembly of custom equipment —
                  serving chemical, pharma, water treatment, and manufacturing
                  sectors.
                </p>

                <div className="space-y-3 mb-6">
                  {[
                    'Advanced FRP manufacturing technology & equipment',
                    'Experienced professionals with deep industry knowledge',
                    'Committed to delivering products on schedule',
                    'Best value for money without compromising quality',
                    'Comprehensive after-sales support and maintenance guidance',
                  ].map((point) => (
                    <div key={point} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                      <span className="text-sm sm:text-base text-gray-700">
                        {point}
                      </span>
                    </div>
                  ))}
                </div>

                <Button
                  variant="action"
                  size="lg"
                  asChild
                  className="w-auto min-w-[160px] px-4 py-2"
                >
                  <Link to="/about">Learn Our Story</Link>
                </Button>
              </div>

              {/* Right – Stats */}
              <div className="grid grid-cols-2 gap-4">
                {stats.map((s) => (
                  <Card key={s.label} className="shadow-card text-center p-5 sm:p-6">
                    <div className="text-2xl sm:text-4xl font-bold text-egyptian-blue mt-2">
                      {s.value}
                    </div>
                    <div className="text-gray-600 text-sm sm:text-base mt-1">
                      {s.label}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* ── FEATURED PRODUCTS ── */}
        <LazyLoad height={200} offset={100}>
          <section className="py-10 sm:py-16 bg-white px-4">
            <Container>
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="font-display font-bold text-2xl sm:text-4xl text-egyptian-blue mb-3">
                  Our Products
                </h2>
                <p className="text-gray-600 text-sm sm:text-base">
                  Industry-leading FRP solutions for critical industrial
                  applications
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                {featuredProducts.map((product) => (
                  <Card
                    key={product.id}
                    className="shadow-card hover:shadow-xl transition-shadow duration-300 group h-full flex flex-col"
                  >
                    <CardContent className="p-4 sm:p-6 flex flex-col h-full">
                      {/* ✅ Real product image from data */}
                      <div className="h-36 sm:h-44 w-full rounded-lg mb-4 overflow-hidden bg-gray-100">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>

                      <Badge variant="secondary" className="mb-2 w-fit">
                        {product.badge}
                      </Badge>

                      <h3 className="font-semibold text-base sm:text-lg text-egyptian-blue mb-2 group-hover:text-violet-blue transition-colors">
                        {product.name}
                      </h3>

                      <p className="text-gray-600 text-xs sm:text-sm mb-4 flex-1">
                        {product.shortDescription}
                      </p>

                      <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mt-auto">
                        <Badge
                          variant="outline"
                          className="text-amber border-amber text-xs"
                        >
                          {product.certification}
                        </Badge>
                        {/* ✅ Link goes to correct product detail page */}
                        <Button
                          variant="enterprise"
                          size="sm"
                          asChild
                          className="w-full sm:w-auto"
                        >
                          <Link to={`/product/${product.id}`}>
                            View Details
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="text-center mt-8">
                <Button variant="action" size="lg" asChild>
                  <Link to="/products">
                    View All Products{' '}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </Container>
          </section>
        </LazyLoad>

        {/* ── CONTACT INFO STRIP ── */}
        {/* <section className="bg-egyptian-blue text-white py-8 px-4">
          <Container>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
              <a
                href="tel:+919825850501"
                className="flex flex-col items-center gap-2 group hover:opacity-80 transition-opacity"
              >
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-amber transition-colors">
                  <Phone className="h-5 w-5" />
                </div>
                <span className="text-xs text-white/70 uppercase tracking-wide">
                  Call
                </span>
                <span className="font-semibold">+91 98258 50501</span>
                <span className="text-xs text-white/60">Speak With Us</span>
              </a>

              <a
                href="mailto:info@ss-plastic.com"
                className="flex flex-col items-center gap-2 group hover:opacity-80 transition-opacity"
              >
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-amber transition-colors">
                  <Mail className="h-5 w-5" />
                </div>
                <span className="text-xs text-white/70 uppercase tracking-wide">
                  Mail
                </span>
                <span className="font-semibold">info@ss-plastic.com</span>
                <span className="text-xs text-white/60">Business Enquiries</span>
              </a>

              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                  <MapPin className="h-5 w-5" />
                </div>
                <span className="text-xs text-white/70 uppercase tracking-wide">
                  Site
                </span>
                <span className="font-semibold">Vapi, Gujarat, India</span>
                <span className="text-xs text-white/60">Manufacturing Base</span>
              </div>
            </div>
          </Container>
        </section> */}

        {/* ── FINAL CTA ── */}
        <section className="py-10 sm:py-16 bg-gradient-hero text-white px-4">
          <Container>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="font-display font-bold text-xl sm:text-3xl md:text-4xl mb-4 sm:mb-6">
                Ready to Get Custom FRP Solutions?
              </h2>
              <p className="text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed text-white/90">
                Talk to our engineering team. Get expert consultation, technical
                specifications, and competitive pricing tailored to your plant
                requirements.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <Button variant="action" size="xl" asChild>
                  <Link to="/contact">Get Expert Consultation</Link>
                </Button>
                <Button variant="trust" size="xl" asChild>
                  <Link to="/products">Browse Product Catalog</Link>
                </Button>
              </div>
            </div>
          </Container>
        </section>

        {/* ── FLOATING WHATSAPP ── */}
        <div className="fixed bottom-3 right-3 sm:bottom-6 sm:right-6 z-50">
          <Button
            size="sm"
            className="shadow-xl bg-green-600 hover:bg-green-700"
            onClick={handleWhatsAppRedirect}
          >
            <svg
              style={{ width: '20px', height: '20px' }}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 128 128"
            >
              <path
                fill="white"
                d="M 64 2 C 29.8 2 2 29.8 2 64 C 2 74.5 4.5992188 84.800391 9.6992188 93.900391 L 4.4003906 113.30078 C 3.5003906 116.40078 4.3992188 119.60039 6.6992188 121.90039 C 8.9992188 124.20039 12.200781 125.10078 15.300781 124.30078 L 35.5 119 C 44.3 123.6 54.099609 126 64.099609 126 C 98.299609 126 126.09961 98.2 126.09961 64 C 126.09961 47.4 119.7 31.899219 108 20.199219 C 96.2 8.4992187 80.6 2 64 2 z M 64 8 C 79 8 93.099609 13.800391 103.59961 24.400391 C 114.19961 35.000391 120.1 49.1 120 64 C 120 94.9 94.9 120 64 120 C 54.7 120 45.399219 117.59922 37.199219 113.19922 C 36.799219 112.99922 36.300781 112.80078 35.800781 112.80078 C 35.500781 112.80078 35.3 112.80039 35 112.90039 L 13.699219 118.5 C 12.199219 118.9 11.200781 118.09922 10.800781 117.69922 C 10.400781 117.29922 9.6 116.30078 10 114.80078 L 15.599609 94.199219 C 15.799609 93.399219 15.700781 92.600391 15.300781 91.900391 C 10.500781 83.500391 8 73.8 8 64 C 8 33.1 33.1 8 64 8 z M 64 17 C 38.1 17 17 38 17 64 C 17 72.3 19.200781 80.4 23.300781 87.5 C 24.900781 90.3 25.3 93.599219 24.5 96.699219 L 21.599609 107.19922 L 32.800781 104.30078 C 33.800781 104.00078 34.800781 103.90039 35.800781 103.90039 C 37.800781 103.90039 39.8 104.40039 41.5 105.40039 C 48.4 109.00039 56.1 111 64 111 C 89.9 111 111 89.9 111 64 C 111 51.4 106.09922 39.599219 97.199219 30.699219 C 88.399219 21.899219 76.6 17 64 17 z M 43.099609 36.699219 L 45.900391 36.699219 C 47.000391 36.699219 48.099219 36.799219 49.199219 39.199219 C 50.499219 42.099219 53.399219 49.399609 53.699219 50.099609 C 54.099219 50.799609 54.300781 51.699219 53.800781 52.699219 C 53.300781 53.699219 53.100781 54.299219 52.300781 55.199219 C 51.600781 56.099219 50.699609 57.100781 50.099609 57.800781 C 49.399609 58.500781 48.6 59.300781 49.5 60.800781 C 50.4 62.300781 53.299219 67.1 57.699219 71 C 63.299219 76 68.099609 77.600781 69.599609 78.300781 C 71.099609 79.000781 71.900781 78.900391 72.800781 77.900391 C 73.700781 76.900391 76.5 73.599609 77.5 72.099609 C 78.5 70.599609 79.500781 70.900391 80.800781 71.400391 C 82.200781 71.900391 89.400391 75.499219 90.900391 76.199219 C 92.400391 76.899219 93.399219 77.300391 93.699219 77.900391 C 94.099219 78.700391 94.100391 81.599609 92.900391 85.099609 C 91.700391 88.499609 85.700391 91.899609 82.900391 92.099609 C 80.200391 92.299609 77.699219 93.300391 65.199219 88.400391 C 50.199219 82.500391 40.7 67.099609 40 66.099609 C 39.3 65.099609 34 58.100781 34 50.800781 C 34 43.500781 37.799219 40 39.199219 38.5 C 40.599219 37 42.099609 36.699219 43.099609 36.699219 z"
              />
            </svg>
            <span className="ml-2">Quick Quote</span>
          </Button>
        </div>
      </div>

      <QuoteModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
    </>
  );
};

export default Index;